import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Bot, Plus, SquarePen, Trash2, TestTube, Key, Check, X, Settings } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
    baseURL: `${API_BASE}/api/superadmin/agents`,
});

api.interceptors.request.use((config) => {
    const token = localStorage.getItem('superadmin_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

export default function AgentManager() {
    const navigate = useNavigate();
    const [agents, setAgents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingAgent, setEditingAgent] = useState(null);
    const [showKey, setShowKey] = useState(null);
    const [form, setForm] = useState({
        name: '',
        persona: '',
        jobdesk: '',
        llm_model: 'qwen2.5:14b',
        tools_scope: ['task'],
        workspace_ids: [],
        endpoint_url: '',
        status: 'idle',
    });
    const [workspaces, setWorkspaces] = useState([]);

    useEffect(() => {
        loadAgents();
        loadWorkspaces();
    }, []);

    async function loadAgents() {
        setLoading(true);
        try {
            const res = await api.get('');
            setAgents(res.data || []);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }

    async function loadWorkspaces() {
        try {
            const res = await axios.get(`${API_BASE}/api/superadmin/workspaces`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('superadmin_token')}` }
            });
            setWorkspaces(res.data || []);
        } catch (e) {
            console.error('Failed to load workspaces:', e);
        }
    }

    function openCreate() {
        setEditingAgent(null);
        setForm({
            name: '',
            persona: '',
            jobdesk: '',
            llm_model: 'qwen2.5:14b',
            tools_scope: ['task'],
            workspace_ids: [],
            endpoint_url: '',
            status: 'idle',
        });
        setShowKey(null);
        setDialogOpen(true);
    }

    function openEdit(agent) {
        setEditingAgent(agent);
        setForm({
            name: agent.name || '',
            persona: agent.persona || '',
            jobdesk: agent.jobdesk || '',
            llm_model: agent.llm_model || 'qwen2.5:14b',
            tools_scope: agent.tools_scope || ['task'],
            workspace_ids: agent.workspace_ids || [],
            endpoint_url: agent.endpoint_url || '',
            status: agent.status || 'idle',
        });
        setShowKey(null);
        setDialogOpen(true);
    }

    async function saveAgent() {
        try {
            if (editingAgent) {
                await api.put(`/${editingAgent.id}`, form);
            } else {
                const res = await api.post('', form);
                if (res.data.api_key) {
                    setShowKey({ id: res.data.id, key: res.data.api_key });
                }
            }
            await loadAgents();
            if (editingAgent) setDialogOpen(false);
        } catch (e) {
            alert(e.response?.data?.detail || 'Failed to save agent');
        }
    }

    async function deleteAgent(id) {
        if (!window.confirm('Delete this agent?')) return;
        try {
            await api.delete(`/${id}`);
            await loadAgents();
        } catch (e) {
            alert(e.response?.data?.detail || 'Failed to delete agent');
        }
    }

    async function testConnection(agent) {
        try {
            const res = await api.post(`/${agent.id}/test-connection`);
            alert(`Connection OK: ${JSON.stringify(res.data.agent_response)}`);
        } catch (e) {
            alert(`Connection failed: ${e.response?.data?.detail || e.message}`);
        }
    }

    async function regenerateKey(agent) {
        if (!window.confirm('Regenerate API key? The old key will be invalidated immediately.')) return;
        try {
            const res = await api.post(`/${agent.id}/regenerate-key`);
            setShowKey({ id: agent.id, key: res.data.api_key });
        } catch (e) {
            alert(e.response?.data?.detail || 'Failed to regenerate key');
        }
    }

    function toggleTool(tool) {
        setForm((prev) => {
            const has = prev.tools_scope.includes(tool);
            return {
                ...prev,
                tools_scope: has
                    ? prev.tools_scope.filter((t) => t !== tool)
                    : [...prev.tools_scope, tool],
            };
        });
    }

    function toggleWorkspace(wsId) {
        setForm((prev) => {
            const has = prev.workspace_ids.includes(wsId);
            return {
                ...prev,
                workspace_ids: has
                    ? prev.workspace_ids.filter((id) => id !== wsId)
                    : [...prev.workspace_ids, wsId],
            };
        });
    }

    const toolsOptions = ['task', 'channel', 'comment', 'file', 'bug_analysis'];
    const statusOptions = ['idle', 'busy', 'paused', 'offline', 'blocked'];

    return (
        <div className="p-6 max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-bold flex items-center gap-2">
                    <Bot className="w-6 h-6" /> AI Agents
                </h1>
                <button
                    onClick={openCreate}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
                >
                    <Plus className="w-4 h-4" /> New Agent
                </button>
            </div>

            {loading ? (
                <div className="text-gray-500">Loading agents...</div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {agents.map((agent) => (
                        <div key={agent.id} className="bg-white rounded-lg shadow p-4 border">
                            <div className="flex items-center justify-between mb-2">
                                <div className="font-semibold text-lg">{agent.name}</div>
                                <span
                                    className={`text-xs px-2 py-1 rounded-full ${
                                        agent.status === 'idle'
                                            ? 'bg-green-100 text-green-800'
                                            : agent.status === 'busy'
                                            ? 'bg-blue-100 text-blue-800'
                                            : agent.status === 'paused'
                                            ? 'bg-yellow-100 text-yellow-800'
                                            : agent.status === 'blocked'
                                            ? 'bg-red-100 text-red-800'
                                            : 'bg-gray-100 text-gray-800'
                                    }`}
                                >
                                    {agent.status}
                                </span>
                            </div>
                            <div className="text-sm text-gray-600 mb-1">Model: {agent.llm_model}</div>
                            <div className="text-sm text-gray-600 mb-1">
                                Skills: {(agent.skills_config || []).filter(s => s.enabled).length}/{(agent.skills_config || []).length} enabled
                            </div>
                            <div className="text-sm text-gray-600 mb-3">
                                Workspaces: {(agent.workspace_ids || []).length} assigned
                            </div>

                            <div className="flex gap-2">
                                <button
                                    onClick={() => navigate(`/superadmin/ai-agents/${agent.id}`)}
                                    className="px-3 py-2 rounded hover:bg-blue-50 text-blue-600 text-sm font-medium flex items-center gap-1"
                                    title="Configure"
                                >
                                    <Settings className="w-4 h-4" /> Configure
                                </button>
                                <button
                                    onClick={() => openEdit(agent)}
                                    className="p-2 rounded hover:bg-gray-100 text-gray-600"
                                    title="Edit"
                                >
                                    <SquarePen className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={() => testConnection(agent)}
                                    className="p-2 rounded hover:bg-gray-100 text-gray-600"
                                    title="Test Connection"
                                >
                                    <TestTube className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={() => regenerateKey(agent)}
                                    className="p-2 rounded hover:bg-gray-100 text-gray-600"
                                    title="Regenerate API Key"
                                >
                                    <Key className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={() => deleteAgent(agent.id)}
                                    className="p-2 rounded hover:bg-red-50 text-red-600"
                                    title="Delete"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {agents.length === 0 && !loading && (
                <div className="text-center text-gray-500 py-12">No agents configured yet.</div>
            )}

            {/* Dialog */}
            {dialogOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-bold">
                                {editingAgent ? 'Edit Agent' : 'Create Agent'}
                            </h2>
                            <button onClick={() => setDialogOpen(false)}>
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {showKey && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                                <div className="text-sm font-semibold text-green-800 mb-1">API Key (copy now — shown once only):</div>
                                <code className="text-sm bg-white px-2 py-1 rounded border block break-all">{showKey.key}</code>
                            </div>
                        )}

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">Name</label>
                                <input
                                    type="text"
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    className="w-full border rounded-lg px-3 py-2"
                                    placeholder="Backend-Bot"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">Persona (Markdown)</label>
                                <textarea
                                    value={form.persona}
                                    onChange={(e) => setForm({ ...form, persona: e.target.value })}
                                    className="w-full border rounded-lg px-3 py-2 h-24"
                                    placeholder="# Persona: Backend Engineer AI..."
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">Jobdesk (Markdown)</label>
                                <textarea
                                    value={form.jobdesk}
                                    onChange={(e) => setForm({ ...form, jobdesk: e.target.value })}
                                    className="w-full border rounded-lg px-3 py-2 h-24"
                                    placeholder="Responsibilities..."
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">LLM Model</label>
                                    <input
                                        type="text"
                                        value={form.llm_model}
                                        onChange={(e) => setForm({ ...form, llm_model: e.target.value })}
                                        className="w-full border rounded-lg px-3 py-2"
                                        placeholder="qwen2.5:14b"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Status</label>
                                    <select
                                        value={form.status}
                                        onChange={(e) => setForm({ ...form, status: e.target.value })}
                                        className="w-full border rounded-lg px-3 py-2"
                                    >
                                        {statusOptions.map((s) => (
                                            <option key={s} value={s}>{s}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">Endpoint URL (optional, for webhook mode)</label>
                                <input
                                    type="text"
                                    value={form.endpoint_url}
                                    onChange={(e) => setForm({ ...form, endpoint_url: e.target.value })}
                                    className="w-full border rounded-lg px-3 py-2"
                                    placeholder="https://agent-server.myapp.com/webhook"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-2">Tools Scope</label>
                                <div className="flex flex-wrap gap-2">
                                    {toolsOptions.map((tool) => (
                                        <button
                                            key={tool}
                                            type="button"
                                            onClick={() => toggleTool(tool)}
                                            className={`px-3 py-1 rounded-full text-sm border ${
                                                form.tools_scope.includes(tool)
                                                    ? 'bg-blue-100 border-blue-300 text-blue-800'
                                                    : 'bg-gray-50 border-gray-200 text-gray-600'
                                            }`}
                                        >
                                            {form.tools_scope.includes(tool) && (
                                                <Check className="w-3 h-3 inline mr-1" />
                                            )}
                                            {tool}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-2">Workspaces</label>
                                <div className="max-h-40 overflow-y-auto border rounded-lg p-2">
                                    {workspaces.map((ws) => (
                                        <label key={ws.id} className="flex items-center gap-2 py-1">
                                            <input
                                                type="checkbox"
                                                checked={form.workspace_ids.includes(ws.id)}
                                                onChange={() => toggleWorkspace(ws.id)}
                                            />
                                            <span className="text-sm">{ws.name}</span>
                                        </label>
                                    ))}
                                    {workspaces.length === 0 && (
                                        <div className="text-sm text-gray-400">No workspaces found.</div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-end gap-2 mt-6">
                            <button
                                onClick={() => setDialogOpen(false)}
                                className="px-4 py-2 rounded-lg border hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={saveAgent}
                                className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white"
                            >
                                {editingAgent ? 'Save Changes' : 'Create Agent'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
